/* eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';
import styled from 'styled-components';
import { zPositions } from '@/constants';
import { CheckboxProps } from 'antd/lib/checkbox';
import {
  Checkbox,
  Input,
  Select,
  Text,
  theme,
  Tooltip,
  Icon,
  Space,
} from 'components';
import { StyledInputContainer, StyledOptionsContainer } from './styled';
import { PhoneType } from '../types';
import { useContactModalState } from '../hooks';

export type PhoneInputValueType = {
  number: string;
  sendSms: boolean;
  mustCall: boolean;
  type: PhoneType;
};

export type PhoneInputProps = {
  value: PhoneInputValueType;
  onChange: (value: PhoneInputValueType) => void;
  label?: string;
  disabled?: boolean;
  showOptions?: boolean;
  placeholder?: string;
  phoneNumberMask?: boolean;
  phoneNumberMaskWithExtension?: boolean;
  error?: string;
  dataCy?: string;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  label,
  disabled,
  showOptions = true,
  placeholder,
  phoneNumberMask,
  phoneNumberMaskWithExtension,
  error,
  dataCy,
}) => {
  const { number, sendSms, mustCall, type } = value;
  const [contactModalState] = useContactModalState();
  const { isMustCallDisabled } = contactModalState;

  const handleOnNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedNumber = e.target.value;

    onChange({
      ...value,
      number: updatedNumber,
      ...(!type && { type: 'Primary' }),
    });
  };

  const createCheckValueHandler =
    (key: string): CheckboxProps['onChange'] =>
    e => {
      const updatedValue = e.target.checked;

      onChange({
        ...value,
        [key]: updatedValue,
      });
    };

  const handleOnTypeChange = (
    typeValue: 'Primary' | 'Mobile' | 'Work' | 'Home',
  ) => {
    onChange({
      ...value,
      type: typeValue,
    });
  };

  return (
    <StyledInputContainer>
      <Input
        label={label}
        value={number}
        onChange={handleOnNumberChange}
        disabled={disabled}
        placeholder={placeholder}
        position="after"
        phoneNumberMask={phoneNumberMask}
        phoneNumberMaskWithExtension={phoneNumberMaskWithExtension}
        error={error}
        data-cy={dataCy}
      />
      {showOptions && (
        <StyledOptionsContainer
          data-cy={dataCy ? `${dataCy}-toggles-container` : undefined}
        >
          <Checkbox
            checked={sendSms}
            onChange={createCheckValueHandler('sendSms')}
            data-cy={dataCy ? `${dataCy}-toggle-sms` : undefined}
          >
            <Text strong>SEND SMS</Text>
          </Checkbox>
          <Space direction="horizontal" size={0}>
            <Checkbox
              disabled={isMustCallDisabled}
              checked={mustCall && !isMustCallDisabled}
              onChange={createCheckValueHandler('mustCall')}
              data-cy={dataCy ? `${dataCy}-toggle-mustcall` : undefined}
            >
              <StyledRedText strong>MUST CALL</StyledRedText>
            </Checkbox>
            <Tooltip
              zIndex={zPositions.contactModalComponents}
              title={
                'Enabling must call will ensure our system prompts ' +
                'those who are actioning showings for this listing to call this contact.'
              }
            >
              <Flex>
                <Icon type="antdInfoCircleFilled" color="black" />
              </Flex>
            </Tooltip>
          </Space>
          <Select
            size="small"
            value={type}
            zIndex={zPositions.contactModalComponents}
            onChange={handleOnTypeChange}
            options={[
              // TODO(CONTACT MODULE): Need to update for compatibility
              { label: 'Primary', value: 'Primary' },
              { label: 'Mobile', value: 'Mobile' },
              { label: 'Work', value: 'Work' },
              { label: 'Home', value: 'Home' },
            ]}
            disabled={disabled}
            data-cy={dataCy ? `${dataCy}-select-type` : undefined}
          />
        </StyledOptionsContainer>
      )}
    </StyledInputContainer>
  );
};

export default PhoneInput;

const StyledRedText = styled(Text)`
  color: ${theme.redBase};
`;

const Flex = styled.div`
  display: flex;
`;
